/**
 * The two browser primitives a save file needs: out of the tab, and back into it.
 *
 * Both are here rather than in the panel so the panel stays a sequence of decisions and this stays
 * the only file that knows about object URLs and hidden inputs. Neither of them decides anything:
 * what a file is allowed to be is `readEnvelope`'s question, and the size cap is checked by the
 * caller before a byte is read.
 *
 * There is no File System Access API here. It is not in every browser this game runs in, and a
 * download plus a picker is the pair that works everywhere, including on a phone.
 */

/** The extension the picker filters on, and the one the export writes. */
export const SAVE_FILE_ACCEPT = '.json,application/json';

/**
 * Hands the file to the browser and revokes the URL afterwards.
 *
 * The anchor is never in the document: a click on a detached element still starts the download, and
 * an element that was never appended cannot be left behind by an exception on the way out.
 */
export function downloadText(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // A revoke in the same task cancels the download in some engines, so it waits a turn.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Opens the file picker and resolves with what was chosen, or null when nothing was.
 *
 * Cancel is a resolve rather than a rejection, because a player changing their mind is not an
 * error. It is detected through `cancel`, which every engine this ships to fires, with a `focus`
 * fallback for the ones that do not: without one, a cancelled picker leaves a promise nobody
 * settles and a panel stuck on "picking".
 */
export function pickJsonFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = SAVE_FILE_ACCEPT;

    let settled = false;
    const finish = (file: File | null): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('focus', onFocus);
      resolve(file);
    };

    function onFocus(): void {
      // The picker is gone and the change event, if there is one, is already queued behind this.
      setTimeout(() => {
        finish(input.files?.[0] ?? null);
      }, 300);
    }

    input.addEventListener('change', () => {
      finish(input.files?.[0] ?? null);
    });
    input.addEventListener('cancel', () => {
      finish(null);
    });
    window.addEventListener('focus', onFocus);
    input.click();
  });
}
